import * as React from "react"
import { format } from "date-fns"
import { it } from "date-fns/locale"
import { Calendar as CalendarIcon, Clock } from "lucide-react"

import { cn } from "../../lib/cn"
import { Button } from "../../components/ui/button"
import { Calendar } from "../../components/ui/calendar"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select"

export interface DatePickerProps {
    date?: Date
    setDate: (date: Date | undefined) => void
    disabled?: boolean
    className?: string
    placeholder?: string
}

export function DatePicker({
    date,
    setDate,
    disabled,
    className,
    placeholder = "Seleziona data"
}: DatePickerProps) {
    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant={"outline"}
                    className={cn(
                        "w-full min-w-0 justify-start overflow-hidden px-3 text-left font-normal",
                        !date && "text-muted-foreground",
                        className
                    )}
                    disabled={disabled}
                >
                    <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                    <span className="truncate">
                        {date ? format(date, "d MMMM yyyy", { locale: it }) : placeholder}
                    </span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-auto max-w-[calc(100vw-1.5rem)] max-h-[80vh] overflow-y-auto p-0" align="start">
                <Calendar
                    mode="single"
                    selected={date}
                    onSelect={setDate}
                    initialFocus
                />
            </DropdownMenuContent>
        </DropdownMenu>
    )
}

export interface DateTimePickerProps {
    date?: Date
    setDate: (date: Date | undefined) => void
    disabled?: boolean
    className?: string
    placeholder?: string
}

export function DateTimePicker({
    date,
    setDate,
    disabled = false,
    className,
    placeholder = "Seleziona data e ora"
}: DateTimePickerProps) {
    const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(date)

    // Sync internal state with prop calls
    React.useEffect(() => {
        setSelectedDate(date)
    }, [date])

    const handleDateSelect = (newDate: Date | undefined) => {
        if (!newDate) {
            setSelectedDate(undefined)
            setDate(undefined)
            return
        }

        // Preserve time if modifying existing date, or set to current time / 00:00
        const currentDate = selectedDate || new Date()
        newDate.setHours(currentDate.getHours())
        newDate.setMinutes(currentDate.getMinutes())

        setSelectedDate(newDate)
        setDate(newDate)
    }

    const handleTimeChange = (type: 'hour' | 'minute', value: string) => {
        if (!selectedDate) return

        const newDate = new Date(selectedDate)
        if (type === 'hour') {
            newDate.setHours(parseInt(value))
        } else {
            newDate.setMinutes(parseInt(value))
        }

        setSelectedDate(newDate)
        setDate(newDate)
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button
                    variant={"outline"}
                    className={cn(
                        "w-full min-w-0 justify-start overflow-hidden px-3 text-left font-normal",
                        !date && "text-muted-foreground",
                        className
                    )}
                    disabled={disabled}
                >
                    <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                    <span className="truncate">
                        {date ? format(date, "dd/MM/yyyy HH:mm", { locale: it }) : placeholder}
                    </span>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-auto p-0" align="start">
                <div className="flex flex-col sm:flex-row">
                    <Calendar
                        mode="single"
                        selected={selectedDate}
                        onSelect={handleDateSelect}
                        initialFocus
                    />
                    <div className="flex flex-col gap-2 p-3 border-t sm:border-l sm:border-t-0">
                        <div className="flex items-center gap-2 mb-2">
                            <Clock className="h-4 w-4 text-muted-foreground" />
                            <span className="text-sm font-medium">Orario</span>
                        </div>
                        <div className="flex gap-2">
                            <Select
                                value={selectedDate?.getHours().toString()}
                                onValueChange={(val) => handleTimeChange('hour', val)}
                                disabled={!selectedDate}
                            >
                                <SelectTrigger className="w-[70px]">
                                    <SelectValue placeholder="HH" />
                                </SelectTrigger>
                                <SelectContent position="popper" className="max-h-[200px]">
                                    {Array.from({ length: 24 }, (_, i) => (
                                        <SelectItem key={i} value={i.toString()}>
                                            {i.toString().padStart(2, '0')}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <span className="flex items-center text-muted-foreground">:</span>
                            <Select
                                value={selectedDate?.getMinutes().toString()}
                                onValueChange={(val) => handleTimeChange('minute', val)}
                                disabled={!selectedDate}
                            >
                                <SelectTrigger className="w-[70px]">
                                    <SelectValue placeholder="MM" />
                                </SelectTrigger>
                                <SelectContent position="popper" className="max-h-[200px]">
                                    {Array.from({ length: 60 }, (_, i) => (
                                        <SelectItem key={i} value={i.toString()}>
                                            {i.toString().padStart(2, '0')}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
